/* eslint-disable @typescript-eslint/no-explicit-any */
import type { NormalizedCacheObject, StoreObject } from '@apollo/client';

export interface CachePersistenceConfig {
  // Entity types to persist (by __typename)
  persistentTypes?: string[];
  // Query operation names to persist
  persistentOperations?: string[];
  // TTL in milliseconds for persistent cache entries
  ttl?: number;
  // Max size of persistent cache in bytes
  maxSize?: number;
}

export interface CacheEntry {
  __cache: { timestamp: number; ttl?: number };
}

export interface PersistentCacheData {
  [key: string]: CacheEntry & {
    [key: string]: any;
  };
}

export class SelectiveCachePersistence {
  private config: Required<CachePersistenceConfig>;

  constructor(config: CachePersistenceConfig = {}) {
    this.config = {
      persistentTypes: config.persistentTypes || [
        'ProcessingPartner',
        'Vendor',
        'User',
        'ProcessorSettings',
        'AnimalSettings',
        'Cutsheet',
      ],
      persistentOperations: config.persistentOperations || [
        'processingPartners',
        'processingPartnersOne',
        'ViewProcessingPartner',
        'userSettingsByUserId',
        'vendorList',
      ],
      ttl: config.ttl || 24 * 60 * 60 * 1000, // 24 hours default
      maxSize: config.maxSize || 5 * 1024 * 1024, // 5MB default
      ...config,
    };
  }

  /**
   * Filter cache data to only include entities we want to persist
   * @param cache - The Apollo Cache object to filter
   * @returns Filtered cache data with only persistent entities
   */
  filterCacheForPersistence(cache: NormalizedCacheObject): PersistentCacheData {
    const persistentData: PersistentCacheData = {};
    const timestamp = Date.now();
    const referencesToInclude = new Set<string>();

    // First pass: collect initial entities to persist
    Object.entries(cache).forEach(([key, value]) => {
      if (key === 'ROOT_QUERY') {
        // For ROOT_QUERY, filter the individual query fields
        const filteredRootQuery = this.filterRootQuery(value as any);
        if (filteredRootQuery && Object.keys(filteredRootQuery).length > 0) {
          persistentData[key] = {
            ...filteredRootQuery,
            __typename: 'Query',
            __cache: {
              timestamp,
              ttl: this.config.ttl,
            },
          };
          // Collect references from ROOT_QUERY
          this.collectReferences(filteredRootQuery, referencesToInclude);
        }
      } else if (this.shouldPersistCacheEntry(key, value)) {
        persistentData[key] = {
          ...value,
          __cache: {
            timestamp,
            ttl: this.config.ttl,
          },
        };
        // Collect references from this entity
        this.collectReferences(value, referencesToInclude);
      }
    });

    // Second pass: recursively include all referenced entities (unlimited depth)
    const recursivelyAddReferences = (
      initialRefs: Set<string>,
    ): { added: number; levels: number; processed: number } => {
      let referencesAdded = 0;
      let totalLevels = 0;
      const referencesToProcess = new Set(initialRefs);
      const processedReferences = new Set<string>();

      while (referencesToProcess.size > 0) {
        totalLevels++;
        const currentLevelRefs = Array.from(referencesToProcess);
        referencesToProcess.clear();

        let levelReferencesAdded = 0;

        for (const refKey of currentLevelRefs) {
          if (processedReferences.has(refKey)) {
            continue; // Already processed
          }
          processedReferences.add(refKey);

          if (cache[refKey] && !persistentData[refKey]) {
            persistentData[refKey] = {
              ...cache[refKey],
              __cache: {
                timestamp,
                ttl: this.config.ttl,
              },
            };
            referencesAdded++;
            levelReferencesAdded++;

            // Find new references from this entity
            const newRefs = new Set<string>();
            this.collectReferences(cache[refKey], newRefs);

            // Add new references to process in next iteration
            newRefs.forEach((newRef) => {
              if (!processedReferences.has(newRef) && !persistentData[newRef]) {
                referencesToProcess.add(newRef);
              }
            });
          }
        }

        // Prevent infinite loops (max 10 levels should be more than enough)
        if (totalLevels >= 10) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn(
              'Cache reference spidering hit max depth limit (10 levels)',
            );
          }
          break;
        }

        // If no new references were added at this level, we're done
        if (levelReferencesAdded === 0) {
          break;
        }
      }

      return {
        added: referencesAdded,
        levels: totalLevels,
        processed: processedReferences.size,
      };
    };

    // Execute the recursive reference collection
    recursivelyAddReferences(referencesToInclude);

    return this.pruneExpiredEntries(persistentData);
  }

  /**
   * Restore cache data from persistent storage, filtering out expired entries
   * @param persistentData - The persistent cache data to restore
   * @returns Restored Apollo cache object
   */
  restoreCacheFromPersistence(
    persistentData: PersistentCacheData,
  ): NormalizedCacheObject {
    const restoredCache: NormalizedCacheObject = {};
    const validData = this.pruneExpiredEntries(persistentData);

    Object.entries(validData).forEach(([key, entry]) => {
      // Remove the __cache metadata when restoring
      const { __cache, ...cacheData } = entry;
      restoredCache[key] = cacheData;
    });

    return restoredCache;
  }

  /**
   * Determine if a cache entry should be persisted
   * @param key - The cache key
   * @param value - The cache value
   * @returns Whether the cache entry should be persisted
   */
  private shouldPersistCacheEntry(
    key: string,
    value: StoreObject | undefined,
  ): boolean {
    // ROOT_QUERY is handled separately in filterCacheForPersistence
    if (key === 'ROOT_QUERY') {
      return false;
    }

    if (key === 'ROOT_MUTATION') {
      return false; // Generally don't persist mutations
    }

    // Check if this is an entity we want to persist by typename
    if (value && typeof value === 'object' && value.__typename) {
      return this.config.persistentTypes.includes(value.__typename);
    }

    // Check for specific cache keys that might be important
    // Apollo Client uses different key patterns like "ProcessingPartner:123"
    const hasRelevantType = this.config.persistentTypes.some((type) =>
      key.startsWith(`${type}:`),
    );

    return hasRelevantType;
  }

  /**
   * Recursively collect all entity references from a cache object
   * @param obj - The cache object to scan for references
   * @param references - Set to collect reference keys
   */
  private collectReferences(obj: any, references: Set<string>): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Handle Apollo Client references (e.g., { __ref: "ProcessingPartner:123" })
    if (obj.__ref && typeof obj.__ref === 'string') {
      references.add(obj.__ref);
      return;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      obj.forEach((item) => this.collectReferences(item, references));
      return;
    }

    // Handle objects - recursively scan all properties
    Object.values(obj).forEach((value) => {
      this.collectReferences(value, references);
    });
  }

  /**
   * Filter ROOT_QUERY to only include persistent operations with non-empty values
   * @param rootQuery - The ROOT_QUERY cache object
   * @returns Filtered ROOT_QUERY with only persistent operations that have non-empty values
   */
  private filterRootQuery(rootQuery: any): any {
    if (!rootQuery || typeof rootQuery !== 'object') {
      return null;
    }

    const filteredQuery: any = {};

    Object.keys(rootQuery).forEach((queryField) => {
      // Handle parameterized queries like "processingPartnersOne({"vendorId":"123"})"
      const cleanQueryName = queryField.split('(')[0];

      if (this.config.persistentOperations.includes(cleanQueryName)) {
        const value = rootQuery[queryField];

        // Only include the query field if it has a non-empty value
        if (!SelectiveCachePersistence.isEmptyValue(value)) {
          filteredQuery[queryField] = value;
        }
      }
    });

    return Object.keys(filteredQuery).length > 0 ? filteredQuery : null;
  }

  /**
   * Check if a value is considered empty and should not be cached
   * @param value - The value to check
   * @returns True if the value is empty and should not be cached
   */
  private static isEmptyValue(value: any): boolean {
    // null or undefined
    if (value == null) {
      return true;
    }

    // Empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    // Empty objects (excluding objects with __typename or __ref which are meaningful)
    if (
      typeof value === 'object' &&
      !value.__typename &&
      !value.__ref &&
      Object.keys(value).length === 0
    ) {
      return true;
    }

    // Empty strings
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }

    return false;
  }

  /**
   * Remove expired entries from persistent cache
   * @param persistentData - The persistent cache data to prune
   * @returns Pruned cache data with only valid entries
   */
  private pruneExpiredEntries(
    persistentData: PersistentCacheData,
  ): PersistentCacheData {
    const now = Date.now();
    const validData: PersistentCacheData = {};

    Object.entries(persistentData).forEach(([key, entry]) => {
      const age = now - entry.__cache.timestamp;
      const ttl = entry.__cache.ttl || this.config.ttl;

      if (age < ttl) {
        validData[key] = entry;
      }
    });

    return validData;
  }

  /**
   * Check if persistent cache size exceeds limit and prune oldest entries
   * @param persistentData - The persistent cache data to prune by size
   * @returns Size-pruned cache data
   */
  pruneBySize(persistentData: PersistentCacheData): PersistentCacheData {
    const dataSize = JSON.stringify(persistentData).length;

    if (dataSize <= this.config.maxSize) {
      return persistentData;
    }

    // Sort by timestamp (oldest first) and remove until under size limit
    const entries = Object.entries(persistentData).sort(
      ([, a], [, b]) => a.__cache.timestamp - b.__cache.timestamp,
    );

    const prunedData: PersistentCacheData = {};
    let currentSize = 0;

    // Add entries from newest to oldest until we hit size limit
    for (let i = entries.length - 1; i >= 0; i--) {
      const [key, entry] = entries[i];
      const entrySize = JSON.stringify({ [key]: entry }).length;

      if (currentSize + entrySize <= this.config.maxSize) {
        prunedData[key] = entry;
        currentSize += entrySize;
      } else {
        break;
      }
    }

    return prunedData;
  }

  /**
   * Create persistence options for apollo3-cache-persist
   * @returns Object with serialize and deserialize functions for cache persistence
   */
  createPersistenceOptions() {
    return {
      serialize: (data: NormalizedCacheObject): string => {
        try {
          const persistentData = this.filterCacheForPersistence(data);
          const prunedData = this.pruneBySize(persistentData);

          return JSON.stringify(prunedData);
        } catch (_error) {
          return JSON.stringify(data); // Fallback to original data
        }
      },

      deserialize: (data: string): NormalizedCacheObject => {
        try {
          const persistentData = JSON.parse(data) as PersistentCacheData;
          const restoredCache =
            this.restoreCacheFromPersistence(persistentData);

          return restoredCache;
        } catch (_error) {
          return JSON.parse(data); // Fallback to original data
        }
      },
    };
  }

  /**
   * Validate a restored cache by checking if all references exist
   * @param cache - The restored cache to validate
   * @returns Validation results with missing references
   */
  validateRestoredCache(cache: NormalizedCacheObject): {
    isValid: boolean;
    totalReferences: number;
    missingReferences: string[];
    referencesByType: Record<string, { total: number; missing: number }>;
  } {
    const allReferences = new Set<string>();
    const missingReferences: string[] = [];
    const referencesByType: Record<string, { total: number; missing: number }> =
      {};

    // Collect all references in the cache
    Object.entries(cache).forEach(([_key, value]) => {
      this.collectReferences(value, allReferences);
    });

    // Check which references are missing
    allReferences.forEach((ref) => {
      const type = ref.split(':')[0];

      if (!referencesByType[type]) {
        referencesByType[type] = { total: 0, missing: 0 };
      }
      referencesByType[type].total++;

      if (!cache[ref]) {
        missingReferences.push(ref);
        referencesByType[type].missing++;
      }
    });

    return {
      isValid: missingReferences.length === 0,
      totalReferences: allReferences.size,
      missingReferences,
      referencesByType,
    };
  }
}

// Export a default configuration
export const defaultCachePersistence = new SelectiveCachePersistence();
